/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { forwardRef, useRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import Picture from 'components/Picture/Picture';
import Loader from 'components/Loader/Loader';
import './Poster.scss';

function getDateTimeDuration(duration) {
  const suffix = ['s', 'm', 'h'];
  return duration
    .split(':')
    .reverse()
    .map(time => parseInt(time, 10))
    .map((time, i) => ({
      time,
      suffix: suffix[i],
    }))
    .filter(({ time }) => time > 0)
    .map(({ time, suffix }) => `${time}${suffix}`)
    .join(' ');
}

/**
 * This is the Poster component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
const Poster = forwardRef(
  ({ loading, picture, text, duration, play, onPlay }, ref) => {
    const buttonRef = useRef();

    useImperativeHandle(ref, () => ({
      focus: () => {
        buttonRef.current.focus();
      },
    }));

    function renderPicture() {
      if (!picture) return null;

      return <Picture {...picture} />;
    }

    function renderDuration() {
      if (!duration) return null;
      return <time dateTime={getDateTimeDuration(duration)}>{duration}</time>;
    }

    function renderText() {
      if (text || duration) {
        return (
          <div className="text">
            <h2 className="h3">{text}</h2>
            {renderDuration()}
          </div>
        );
      }

      return null;
    }

    function renderButton() {
      if (loading) {
        return (
          <div className="button-play">
            <Loader />
          </div>
        );
      }

      return (
        <button
          ref={buttonRef}
          aria-label={play}
          title={play}
          className="button-play"
          onClick={onPlay}
        >
          <span />
        </button>
      );
    }

    return (
      <div className="poster" onClick={onPlay}>
        {renderPicture()}
        <div className="overlay" title={play}>
          <div>
            {renderButton()}
            {renderText()}
          </div>
        </div>
      </div>
    );
  }
);

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Poster.propTypes = {
  loading: PropTypes.bool,
  text: PropTypes.string,
  duration: PropTypes.string,
  picture: PropTypes.object,
  play: PropTypes.string.isRequired,
  onPlay: PropTypes.func.isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Poster.defaultProps = {
  loading: false,
  duration: null,
  picture: null,
  play: 'Play',
  onPlay: () => {},
};

export default Poster;
